import React from "react";
import { Card, Col, Divider, Row, Space, Table, Tag, Typography } from "antd";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";
import moment from "moment";
import { getById } from "../../services/invoices.service";
import {
  formatCurrency,
  formatDecimal,
  formatInvoiceDate,
  showCpf,
} from "../../services/utils";

const { Text, Title } = Typography;

export default function InvoiceDetail() {
  const { uuid } = useParams();

  const { data: invoiceResponse } = useQuery(
    ["loadInvoice", uuid],
    () => (uuid ? getById(uuid) : null),
    { refetchOnWindowFocus: false }
  );

  const invoice = invoiceResponse?.data || {};
  const account = invoice?.sale?.account || {};

  const totalItems = invoice?.items?.reduce(
    (total: number, item: any) =>
      total + Number(item.price || 0) * Number(item.quantity || 0),
    0
  );

  const discountValue = Math.max(
    0,
    Number(totalItems) - Number(invoice?.total_amount || 0)
  );

  const columns = [
    { title: "Serviço", dataIndex: ["service", "name"], key: "service" },
    {
      title: "Valor",
      dataIndex: "unit_price",
      key: "unit_price",
      render: (value: any) => formatCurrency(parseFloat(value || 0)),
      align: "right" as const,
      width: 120,
    },
    {
      title: "Qtd",
      dataIndex: "quantity",
      key: "quantity",
      render: (value: any) => formatDecimal(value || 0),
      align: "right" as const,
      width: 80,
    },
    {
      title: "Total R$",
      key: "total",
      render: (_: any, record: any) =>
        formatCurrency((record?.unit_price || 0) * (record?.quantity || 0)),
      align: "right" as const,
      width: 140,
    },
  ];

  const statusLabel = (() => {
    const s = String(invoice?.status || "");
    if (["Paid", "Paga"].includes(s)) return "Paga";
    if (["Pending", "Pendente"].includes(s)) return "Pendente";
    if (["Canceled", "Cancelada"].includes(s)) return "Cancelada";
    return s || "";
  })();

  const statusColor = (() => {
    const s = statusLabel;
    if (s === "Paga") return "success";
    if (s === "Pendente") return "warning";
    if (s === "Cancelada") return "error";
    return "default";
  })();

  return (
    <div style={{ maxWidth: 660, margin: "0 auto" }}>
      <Card
        styles={{
          header: { color: "#107B99", fontWeight: 600 },
          body: { padding: 0 },
        }}
        title="Fatura"
      >
        <div style={{ padding: 24 }}>
          <Row gutter={16} align="middle">
            <Col xs={{ span: 18 }} sm={{ span: 18 }} md={{ span: 18 }}>
              <Space direction="vertical" size={2}>
                <img
                  src={account?.thumbnail}
                  alt={account?.name}
                  style={{ height: 86, objectFit: "contain" }}
                />
                <Text strong>{account?.name || ""}</Text>
                <Text type="secondary">
                  {account?.settings?.document || ""}
                </Text>
              </Space>
            </Col>
            <Col
              xs={{ span: 6 }}
              sm={{ span: 6 }}
              md={{ span: 6 }}
              style={{ textAlign: "right" }}
            >
              {statusLabel && (
                <Tag
                  color={statusColor}
                  style={{ borderRadius: 20, padding: "2px 12px" }}
                >
                  {statusLabel}
                </Tag>
              )}
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }} />

          <Row gutter={16}>
            <Col xs={{ span: 16 }} sm={{ span: 16 }} md={{ span: 16 }}>
              <Space direction="vertical">
                <Text type="secondary">Cliente</Text>
                <Text strong>{invoice?.customer?.name || ""}</Text>
                {invoice?.customer?.document && (
                  <Text strong>
                    {invoice?.customer?.document_type === "CPF"
                      ? showCpf(invoice?.customer?.document)
                      : invoice?.customer?.document}
                  </Text>
                )}
                <Text type="secondary">Data de emissão</Text>
                <Text strong>
                  {invoice?.billing_date
                    ? moment(invoice?.billing_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
              </Space>
            </Col>

            <Col
              xs={{ span: 8 }}
              sm={{ span: 8 }}
              md={{ span: 8 }}
              style={{ textAlign: "right" }}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text type="secondary">Fatura</Text>
                <Text strong>{invoice?.number || invoice?.id || ""}</Text>
                <Text type="secondary">Vencimento</Text>
                <Text strong>
                  {invoice?.due_date
                    ? moment(invoice?.due_date, "YYYY-MM-DD").format(
                        "DD/MM/YYYY"
                      )
                    : ""}
                </Text>
                <Text type="secondary">Valor</Text>
                <Title level={5} style={{ margin: 0 }}>
                  {formatCurrency(parseFloat(invoice?.total_amount || 0))}
                </Title>
              </Space>
            </Col>
          </Row>

          <Divider style={{ margin: "16px 0" }} />

          <Table
            dataSource={invoice?.items}
            columns={columns}
            pagination={false}
            rowKey={(record: any, idx) =>
              record?.service_id || record?.service?.id || String(idx)
            }
            size="small"
          />

          <Row style={{ padding: "12px 24px" }}>
            <Col span={16}>
              <Space direction="vertical">
                {invoice?.description && (
                  <Text type="secondary">{invoice?.description}</Text>
                )}
              </Space>
            </Col>
            <Col span={8} style={{ textAlign: "right" }}>
              <Space
                direction="vertical"
                style={{ width: "100%", alignItems: "flex-end" }}
              >
                <Text type="secondary">Desconto</Text>
                <Text>{formatCurrency(discountValue || 0)}</Text>
                <Text strong style={{ marginTop: 8 }}>
                  {formatCurrency(parseFloat(invoice?.total_amount || 0))}
                </Text>
              </Space>
            </Col>
          </Row>

          <Row>
            <Col>
              <Text>
                Competência: {formatInvoiceDate(invoice?.billing_date)}
              </Text>
            </Col>
          </Row>

          <Divider style={{ margin: "8px 0" }} />

          {statusLabel === "Paga" && (
            <Row style={{ padding: 24 }} justify="center">
              <Col style={{ textAlign: "center" }}>
                <div
                  style={{
                    width: 64,
                    height: 64,
                    borderRadius: 32,
                    margin: "0 auto 12px",
                    backgroundColor: "#52c41a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#fff",
                    fontSize: 32,
                  }}
                >
                  ✓
                </div>
                <Title level={5} style={{ margin: 0 }}>
                  Pagamento confirmado!
                </Title>
                {(invoice?.uuid && invoice?.payments?.length && invoice?.payments?.[0]?.transaction_id) ? (
                  <Text type="secondary">
                    Código da transação:
                    <br />
                    {invoice?.payments?.[0]?.transaction_id}
                  </Text>
                ) : ''}
              </Col>
            </Row>
          )}
        </div>
      </Card>
    </div>
  );
}
